import api, { methods, assetsUrl } from "@api";
import Spacer from "@components/Spacer";
import Button from "@components/button";
import { useDownloadFile } from "@hooks/use-download-file";
import RadioButtonGroup from "@components/radio-button-group";
import { mandateRequestParams, openMandate } from "@features/auth/browser";
import { finishMandate } from "@features/auth/mandate-result";
import Error from "@features/error/error";
import { useIssuersList, useMandateProvider } from "@provider/app-data";
import { useAuth } from "@provider/context-auth";
import { toast } from "@provider/toast";
import { useMutation } from "@tanstack/react-query";
import type { Account } from "@type/account";
import { paymentTypes } from "@utils";
import { Text, View } from "dripsy";
import * as React from "react";
import { useFormState } from "react-native-use-form";
import { bankIcons } from "@assets/banks";

export default function AccountChangePayment({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { download, isLoading: downloadLoading, statusLabel } = useDownloadFile();
  const { data: issuersList, isError: isIssuersError, refetch: refetchIssuers } = useIssuersList();
  const { profile } = useAuth();
  // Met Mollie kiest de klant zijn bank bij Mollie (iDEAL voor NL, Bancontact
  // voor BE): geen eigen bankenlijst en geen PDF-formulier, één knop.
  const provider = useMandateProvider();
  const isMollie = provider === "mollie";

  const isSepa = profile?.payment_type! <= 2;
  const noMandate = isSepa && !profile?.pay_with_sepa;

  const [form, inputs] = useFormState(
    {
      paymentType: profile?.payment_type!,
      mandateBank: profile?.mandate_bank
        ? profile?.mandate_bank.DebtorBankId
        : (issuersList?.Nederland[0]?.DebtorBankId ?? ""),
    },
    {
      onSubmit: ({ mandateBank }) => {
        if (isMollie) {
          startMandate();
          return;
        }
        update({ data: issuersList?.Nederland.find((b) => b.DebtorBankId === mandateBank) });
      },
    },
  );

  const { mutate: getMandateRequestUrl, isPending: isMandateLoading } = useMutation({
    mutationKey: ["mandate-request"],
    mutationFn: ({ token, paymentType }: { token?: string; paymentType: number }) =>
      api<{ url: string; error: any }>({
        path: "/mandate-request",
        method: methods.GET,
        // Met Mollie bepaalt de gekozen betaalwijze de methode (1 = iDEAL,
        // 2 = Bancontact) en daarmee de payment_type na de machtiging; zonder
        // die keuze nam de server de oude payment_type uit het profiel.
        params: isMollie
          ? { ...mandateRequestParams(provider), payment_type: String(paymentType) }
          : mandateRequestParams(provider),
        token,
      }),
    onError: (err) => {
      console.log("err", err);
      toast.show(err?.message || "Er is iets fout gegaan.");
    },
  });

  const openMandateUrl = async (url: string) => {
    const result = await openMandate(url, provider);
    if (result === "doorgestuurd") {
      // web: dit tabblad gaat naar Mollie; de melding volgt na de terugkeer
      return;
    }
    // Wat er echt staat, komt uit het profiel (de webhook kan het al
    // opgeslagen hebben, ook als de klant de browser zelf sloot).
    await finishMandate(result);
    onClose();
    // Bij Mollie is de machtiging meteen rond (de toast meldt de uitkomst);
    // de banner "je aanvraag is in behandeling" hoort alleen bij eMandates.
    if (!isMollie) {
      onSuccess();
    }
  };

  const startMandate = (token?: string) =>
    getMandateRequestUrl(
      { token, paymentType: form.values.paymentType },
      {
        onSuccess: (suc) => openMandateUrl(suc.url),
      },
    );

  const { mutate: update, isPending: isLoading } = useMutation({
    mutationKey: ["update-bankdeptor"],
    mutationFn: (body: { data: any }) =>
      api<Account>({
        path: "/account/update-bankdebtor",
        body,
        method: methods.POST,
      }),
    onError: () => {
      toast.show("Er is iets fout gegaan.");
    },
    onSuccess: async (res) => startMandate(res.api_token),
  });

  const selectedType = form.values.paymentType;
  // eMandates: alleen NL online (bankenlijst); BE via het PDF-formulier.
  // Mollie: NL (iDEAL) en BE (Bancontact) allebei online.
  const canMandateOnline = noMandate && (selectedType === 1 || (isMollie && selectedType === 2));
  const showBankList = !isMollie && canMandateOnline;
  const showPdfForm = !isMollie && selectedType === 2;

  return (
    <>
      <RadioButtonGroup
        {...inputs.raw("paymentType", {
          required: true,
        })}
        values={paymentTypes.map((label, i) => {
          return { label, id: i + 1 };
        })}
      />

      {noMandate ? (
        <Error paddingTop="$1" paddingBottom="$3" error="(Nog geen machtiging gegeven)" isError={noMandate} />
      ) : (
        <Text sx={{ paddingTop: 6, paddingBottom: "$3" }}>
          {isSepa
            ? `Je hebt ons via een digitale SEPA- machtiging toestemming gegeven om de facturen door middel van automatische incasso (domiciliëring) te innen.`
            : `Je betaald door middel van iDeal. De Grote Verleiding biedt je ook de mogelijkheid om gemakkelijk te betalen door middel van automatische incasso (domiciliëring). Klik hieronder op 'Betaalgegevens wijzigen' om de betaalwijze aan te passen.`}
        </Text>
      )}
      {showBankList ? (
        <>
          <Text sx={{ fontSize: 16, fontWeight: "bold", color: "$text", mb: "$2" }}>Selecteer bank</Text>
          {isIssuersError && (
            <Button outline onPress={() => refetchIssuers()}>
              Banken ophalen mislukt, probeer opnieuw
            </Button>
          )}
          {!issuersList?.Nederland?.length && !isIssuersError && (
            <Text sx={{ fontSize: 14, color: "$textSecondary", py: "$2" }}>Geen banken beschikbaar</Text>
          )}
          <RadioButtonGroup
            values={
              issuersList?.Nederland.map((bank) => ({
                id: bank.DebtorBankId,
                label: bank.DebtorBankName,
                icon: bankIcons[bank.DebtorBankId],
              })) ?? []
            }
            {...inputs.raw("mandateBank", {
              required: true,
            })}
          />
        </>
      ) : null}
      {isMollie && canMandateOnline ? (
        <Text sx={{ fontSize: 14, color: "$textSecondary", pb: "$2" }}>
          {selectedType === 2
            ? "Je geeft de machtiging met een betaling van € 0,02 via Bancontact. Je kiest je bank op de volgende pagina."
            : "Je geeft de machtiging met een betaling van € 0,01 via iDEAL. Je kiest je bank op de volgende pagina."}
        </Text>
      ) : null}

      {showPdfForm ? (
        <View sx={{ mb: "$1" }}>
          <Button
            onPress={() => {
              download({
                url: assetsUrl + "/files/Machtiging_Domiciliering.pdf",
                fileName: "Machtiging_Domiciliering.pdf",
                withToken: false,
              });
            }}
            outline
            isLoading={downloadLoading}
            description={statusLabel}
          >
            Download machtigingsformulier
          </Button>
        </View>
      ) : null}
      <Spacer size="$4" />
      {canMandateOnline ? (
        <Button color="$primary" isLoading={isLoading || isMandateLoading} onPress={form.submit}>
          Geef machtiging
        </Button>
      ) : null}
    </>
  );
}
